/**
 * Trunnion AI - Template Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/config/queryKeys';
import { templatesApi } from '@/api/templates.api';
import { useNotificationStore } from '@/stores/notificationStore';
import type {
  TemplateFilters,
  TemplateInstantiateRequest,
  SaveAsTemplateRequest,
} from '@/types/template.types';

export function useTemplates(filters: TemplateFilters = {}) {
  return useQuery({
    queryKey: queryKeys.templates.list(filters as Record<string, unknown>),
    queryFn: () => templatesApi.list(filters),
  });
}

export function useTemplate(id: string) {
  return useQuery({
    queryKey: queryKeys.templates.detail(id),
    queryFn: () => templatesApi.getById(id),
    enabled: !!id,
  });
}

export function useInstantiateTemplate() {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: ({
      templateId,
      request,
    }: {
      templateId: string;
      request: TemplateInstantiateRequest;
    }) => templatesApi.instantiate(templateId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.templates.all });
      addNotification({
        type: 'success',
        title: 'Workflow Created',
        message: 'New workflow has been created from template.',
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Instantiation Failed',
        message: error.message,
      });
    },
  });
}

export function useSaveAsTemplate() {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: ({
      workflowId,
      request,
    }: {
      workflowId: string;
      request: SaveAsTemplateRequest;
    }) => templatesApi.saveAsTemplate(workflowId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.templates.all });
      addNotification({
        type: 'success',
        title: 'Template Saved',
        message: 'Workflow has been saved as a template.',
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Save Failed',
        message: error.message,
      });
    },
  });
}
